// TimelineEventCard — single event card inside the 3-section timeline
// (W3.1 §5.7). 3 visual states (is_today / is_past / future), milestone vs
// anniversary borders, optional 「交往紀念日」 / 「預設節日」 pills.
//
// W45.0 `ADR-0114` — **every card is a real `<Link>`, whatever its date.**
//
// The bug this replaces attached the handler conditionally:
//
//     onClick={isFuture && event.kind !== "milestone" ? handleActivate : undefined}
//
// `isFuture` is a **display** state — the same flag drives `opacity-60` for past
// and the `ring-2` for today — so a visual treatment had leaked into the
// interaction state and three whole kinds of event were dead: past, today, and
// milestones. On a real account the timeline is mostly past years, so
// 「每個事件點不了進 detail」 is the accurate description, not an exaggeration.
//
// The two concerns are now separate. `isPast / isToday / isFuture` style the
// card and nothing else.

import { Link } from "react-router-dom";
import { Cake, Gift, Heart, Plane, Sparkles, Sun, Calendar } from "lucide-react";

import {
  countdownLabel,
  daysUntil,
} from "@/lib/anniversary/countdown";
import { categoryIcon } from "@/lib/anniversary/categoryIcon";
import { Card } from "@/components/ui/card";
import { usePrefersReducedMotion } from "@/lib/anniversary/countUp";
import type { AnniversaryTimelineEvent } from "@/lib/api/anniversary";
import { cn } from "@/lib/utils/cn";

export interface TimelineEventCardProps {
  event: AnniversaryTimelineEvent;
}

export function TimelineEventCard({ event }: TimelineEventCardProps) {
  const reduced = usePrefersReducedMotion();
  const Icon =
    event.kind === "milestone"
      ? Sparkles
      : categoryIcon(event.category);
  const days = event.days_until ?? daysUntil(event.date);
  const isPast = days < 0;
  const isToday = event.is_today || days === 0;

  const stateLabel = isToday ? "is_today" : isPast ? "is_past" : "future";
  const ariaLabel = `${event.title}, ${countdownLabel(days)}`;

  /**
   * Where this card goes.
   *
   * A milestone's own `id` is an `AnniversaryMilestone` primary key — measured,
   * and documented in `backend/.../service/timeline.py` as `id=ms.id`. There is
   * no `/anniversaries/{milestone_id}`, so linking to `event.id` would 404. The
   * backend now serialises the parent (`anniversary_id`), and the milestone
   * resolves to **its** anniversary's detail page.
   *
   * `?? null` at the end is a deliberate floor rather than a fallback: a
   * milestone whose parent is somehow absent is a data fault, and rendering a
   * non-link card for it is the honest response. Navigating to a 404 is not.
   */
  const to =
    event.kind === "milestone"
      ? (event.anniversary_id ? `/anniversaries/${event.anniversary_id}` : null)
      : `/anniversaries/${event.id}`;

  const body = (
    <>
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cn(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            event.kind === "milestone"
              ? "bg-shared-tint text-shared-strong"
              : event.is_anchor
                ? "bg-gold-tint text-gold-ink"
                : "bg-you-tint text-you-strong",
          )}
        >
          <Icon size={18} strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-body font-semibold text-ink-1 truncate">
              {event.title}
            </h3>
            {event.is_anchor ? (
              <span
                className="rounded-pill bg-gold-tint px-1.5 py-0.5 text-micro text-gold-ink font-semibold"
                data-testid="anchor-pill"
              >
                交往紀念日
              </span>
            ) : null}
            {event.category === "festival" ? (
              <span className="rounded-pill bg-paper-tint1 px-1.5 py-0.5 text-micro text-ink-2">
                預設節日
              </span>
            ) : null}
          </div>
          <p className="text-meta text-ink-3 tabular-nums">{event.date}</p>
          <p
            className="text-body-sm text-ink-2 font-semibold tabular-nums"
            data-testid="event-countdown"
          >
            {countdownLabel(days)}
          </p>
        </div>
      </div>
      {/* The affordance. A card that navigates has to look like it: a press-only
          state is a state a touch user only discovers by pressing, and the
          W45.1 card-in-card rule rules out carrying it as a hover background. */}
      <ChevronGo />
      {/* keep import tree-shake-resistant */}
      <span className="hidden" aria-hidden>
        <Cake size={14} />
        <Gift size={14} />
        <Heart size={14} />
        <Plane size={14} />
        <Sun size={14} />
        <Calendar size={14} />
      </span>
    </>
  );

  // W52.0 D-3 — the fill was **absent**, not transparent.
  //
  // This class string said `glass`, and nothing in this project defines that:
  // `globals.css` has `.glass-1` / `.glass-2` / `.glass-thick` /
  // `.surface-glass`, and `tailwind.config.ts` has `backdropBlur.glass1 /
  // glass2`. Tailwind emits nothing for a class it does not know, so every
  // card rendered with **no background at all** and the page ground showed
  // through — then `opacity-60` was stacked on top of that.
  //
  // `surface-plain` is `rgb(var(--surface-white-rgb))`, an opaque fill, and the
  // rule that declares it is asserted to exist in `w52-event-card-fill.test.tsx`.
  // Same family as W51's `bg-lilac` (`b20f7d5`): `tsc` accepts any string in
  // `className`, so only the emitted CSS can tell you a class is fake.
  //
  // W53.2 D-1/D-2 — the **surface** half of this string moved into `<Card>`,
  // which is where `surface-plain` / `rounded-2xl` / `border-hairline` /
  // `shadow-card` now live. What is left here is only what is genuinely this
  // card's own: `relative` (the chevron and the press scale need it), the
  // layout, the transition, the milestone/today border treatment.
  //
  // It had **no shadow at all** before, next to `WishBlocks` which had one —
  // same "L2 white card" role, same screen family, one shadow apart. That was
  // D-2, and it is what `hard-rule-audit.mjs` cannot see: the string was legal
  // Tailwind, just incomplete.
  const chrome = cn(
    "relative flex flex-col gap-2",
    "border transition-colors duration-quick ease-paper",
    event.kind === "milestone"
      ? "border-dashed border-shared/40"
      : "border-you/15",
    // `bg-shared/5` used to ride along here and was **dead**: `.surface-plain`
    // is an unlayered rule, so a Tailwind utility on the same element loses to
    // it. The ring alone carries the today state, which is what was actually
    // rendering before this change.
    isToday && "ring-2 ring-shared",
    // `isPast` no longer drives opacity. Past and future are told apart by the
    // **icon colour** and the **countdown wording** — 「410 天前」 reads without
    // a fade, and a faded card is the one state that can still be mistaken for
    // disabled. `opacity` also stays out of `pointer-events` and the tab order
    // for the reason `ADR-0114` gave.
    !reduced && "transition-transform duration-quick ease-paper active:scale-[0.98]",
  );

  if (!to) {
    return (
      <Card
        as="article"
        data-component="TimelineEventCard"
        data-testid="anniversary-card"
        data-state={stateLabel}
        data-kind={event.kind}
        data-anchor={event.is_anchor ? "true" : undefined}
        data-unlinked="true"
        aria-label={ariaLabel}
        className={cn(chrome, "p-s-5 pr-14")}
      >
        {body}
      </Card>
    );
  }

  // A navigable card is a react-router `<Link>`, so the `<Card>` wraps the
  // `<Link>` rather than the other way round — see `Card`'s `as` prop for why
  // `a` is not an option there. `overflow-hidden` keeps the link's rounded
  // corners from spilling past the card's.
  //
  // W53.2 D-2 — **the identifying attributes sit on the `<Card>`, not the link.**
  // They used to sit on the link, which was correct when the link *was* the
  // painted surface, and silently wrong once it was not: `data-testid` pointed at
  // an element with no `surface-plain`, and `[data-component]` resolved to a
  // `div` while `EventCard.test.tsx` asserted `tagName === "A"` on it. Two guards
  // went red for the right reason and the fix was to attach the identity to the
  // card that actually paints it.
  return (
    <Card
      className={cn(chrome, "overflow-hidden")}
      data-component="TimelineEventCard"
      data-testid="anniversary-card"
      data-state={stateLabel}
      data-kind={event.kind}
      data-anchor={event.is_anchor ? "true" : undefined}
      aria-label={ariaLabel}
    >
      <Link
        to={to}
        // `data-tappable` so a test can ask the question that matters — 「can this
        // one be opened?」 — per event, rather than counting handlers.
        data-tappable="true"
        className="flex flex-col gap-2 p-s-5 pr-14 press"
      >
        {body}
      </Link>
    </Card>
  );
}

/** 30px is the *visual* affordance; the card itself is the target. */
function ChevronGo() {
  return (
    <span
      aria-hidden
      data-testid="timeline-event-go"
      className="pointer-events-none absolute right-s-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-hairline bg-paper/70"
    >
      <svg
        className="h-3.5 w-3.5 text-ink-2"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </span>
  );
}